/* eslint-disable @typescript-eslint/naming-convention */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { PER_PAGE_COUNT } from './constants';
import {
  FilterValues,
  PeoplePageFetchState,
  PeoplePageRequestParams,
  PeoplePageResponse,
  PersonDetailsFetchState,
  PersonDetailsRequestParams,
  PersonDetailsResponse,
  SortValues,
} from './customPeopleTagTypes';
import { usePeoplePageSearchState } from './hooks/usePeoplePageQueryState';
import { usePersonDetailsQueryState } from './hooks/usePersonDetailsQueryState';

const cleanPayload = (
  requestParams: PeoplePageRequestParams
): PeoplePageRequestParams =>
  Object.keys(requestParams).reduce((acc, key) => {
    if (!!requestParams[key]) {
      return { ...acc, [key]: requestParams[key] };
    }
    return;
  }, {} as PeoplePageRequestParams);

export const useFetchPeoplePageData = (
  orgApiKey: string
): PeoplePageFetchState => {
  const [peopleData, setPeopleData] = useState<PeoplePageResponse>(undefined);
  const [peopleDataLoading, setPeopleDataLoading] = useState(undefined);
  const [initialLoadingState, setInitialLoadingState] = useState(true);
  const [peopleDataError, setPeopleDataError] = useState(true);
  const [state] = usePeoplePageSearchState();
  const perPage = PER_PAGE_COUNT;

  useEffect(() => {
    const generateData = async (): Promise<void> => {
      const requestParams = {
        asset_id: state.ids?.split(', '),
        page_num: Number(state.page) || 1,
        per_page_count: perPage,
        sort_by: (state.sort as SortValues) || SortValues.Newest,
        status: (state.filter as FilterValues) || FilterValues.Untagged,
      };
      const payload = cleanPayload(requestParams);

      try {
        setPeopleDataLoading(true);
        const response = await fetch(
          `${BFG.DATA_INSIGHTS_URL}/people-tagging/people-page-faces`,
          {
            method: 'POST',
            mode: 'cors',
            headers: {
              'Content-Type': 'application/json',
              Accept: '*/*',
              Authorization: `Bearer ${orgApiKey}`,
            },
            body: JSON.stringify({
              // using hard coded keys for now
              org_key: 'p2t7i2-f2stwo-dex16s',
              bf_key: 'mbxwk7grgtpsxzwnjc3hpj7j',
              ...payload,
            }),
          }
        );

        if (response.status !== 200) throw new Error();
        const peopleDataResponse = await response.json();
        setPeopleData(peopleDataResponse);
      } catch (error) {
        setPeopleData(undefined);
        setPeopleDataError(true);
      } finally {
        setPeopleDataLoading(false);
        setInitialLoadingState(false);
      }
    };
    generateData();
  }, [
    state.filter,
    state.page,
    state.search,
    state.sort,
    state.ids,
    perPage,
    orgApiKey,
  ]);

  return {
    peopleData,
    peopleDataLoading,
    initialLoadingState,
    peopleDataError,
  };
};

export const useFetchPersonDetailsData = (
  orgApiKey: string
): PersonDetailsFetchState => {
  const { id: face_id } = useParams();
  const [state] = usePersonDetailsQueryState();
  const [personDetailsLoading, setPersonDetailsLoading] = useState(true);
  const [personDetailsError, setPersonDetailsError] = useState(true);
  const [personDetailsData, setPersonDetailsData] =
    useState<PersonDetailsResponse>(undefined);
  const perPage = PER_PAGE_COUNT;

  useEffect(() => {
    const generateData = async (): Promise<void> => {
      setPersonDetailsLoading(true);
      const payload: PersonDetailsRequestParams = {
        // using hard coded ID for now, replace with face_id
        face_id: 'face_m37n584w9664s8kvhq63k_416_1875',
        page_num: Number(state.page) || 1,
        per_page_count: perPage,
        sort_by: (state.sort as SortValues) || SortValues.Newest,
      };
      try {
        const response = await fetch(
          `${BFG.DATA_INSIGHTS_URL}/people-tagging/person-details`,
          {
            method: 'POST',
            mode: 'cors',
            headers: {
              'Content-Type': 'application/json',
              Accept: '*/*',
              Authorization: `Bearer ${orgApiKey}`,
            },
            body: JSON.stringify({
              // using hard coded keys for now
              org_key: 'p2t7i2-f2stwo-dex16s',
              bf_key: 'mbxwk7grgtpsxzwnjc3hpj7j',
              ...payload,
            }),
          }
        );

        if (response.status !== 200) throw new Error();

        const personDetailsResponse: PersonDetailsResponse =
          await response.json();
        setPersonDetailsData(personDetailsResponse);
      } catch (error) {
        setPersonDetailsData(undefined);
        setPersonDetailsError(true);
      } finally {
        setPersonDetailsLoading(false);
      }
    };
    generateData();
  }, [state.page, state.sort, orgApiKey, perPage]);

  return {
    personDetailsData,
    personDetailsError,
    personDetailsLoading,
  };
};
